import { useEffect, useState } from "react";
import { Heart, MoonStar, Star } from "lucide-react";
import lunaBandanaAsset from "@/assets/luna-bandana.jpg.asset.json";
import lunaAutoAsset from "@/assets/luna-auto.jpg.asset.json";
import lunaArteAsset from "@/assets/luna-arte.jpg.asset.json";

const LUNA_PHOTOS = [
  {
    src: lunaBandanaAsset.url,
    alt: "Luna, una perrita negra con pañuelo rojo, sonriendo a la cámara",
  },
  {
    src: lunaAutoAsset.url,
    alt: "Luna viajando feliz en el asiento trasero del auto junto a su dueña",
  },
  {
    src: lunaArteAsset.url,
    alt: "Retrato artístico en líneas blancas de Luna mirando hacia el cielo",
  },
] as const;

const ROTATE_MS = 4500;

export function LunaTribute() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % LUNA_PHOTOS.length),
      ROTATE_MS,
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <section id="luna" className="relative overflow-hidden bg-night py-20 sm:py-28">
      {/* Twinkling stars */}
      <Star
        aria-hidden
        fill="currentColor"
        className="absolute left-[8%] top-16 h-4 w-4 animate-twinkle text-accent/50"
      />
      <Star
        aria-hidden
        fill="currentColor"
        className="absolute right-[12%] top-24 h-5 w-5 animate-twinkle text-accent/40 [animation-delay:1.4s]"
      />
      <Star
        aria-hidden
        fill="currentColor"
        className="absolute bottom-20 left-[45%] h-3.5 w-3.5 animate-twinkle text-accent/30 [animation-delay:2.4s]"
      />

      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="relative aspect-square overflow-hidden rounded-[2.5rem] shadow-card ring-1 ring-white/10">
            {LUNA_PHOTOS.map((photo, i) => (
              <img
                key={photo.src}
                src={photo.src}
                alt={i === active ? photo.alt : ""}
                loading={i === 0 ? "eager" : "lazy"}
                width={1024}
                height={1024}
                aria-hidden={i !== active}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
                  i === active ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
          </div>

          {/* Carousel dots */}
          <div className="absolute -bottom-7 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-night/80 px-2 py-1 ring-1 ring-white/10 backdrop-blur-sm">
            {LUNA_PHOTOS.map((photo, i) => (
              <button
                key={photo.src}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Ver foto ${i + 1} de Luna`}
                aria-current={i === active}
                className="flex h-12 w-12 items-center justify-center rounded-full"
              >
                <span
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === active ? "w-6 bg-accent" : "w-2 bg-white/30"
                  }`}
                />
              </button>
            ))}
          </div>


          <span className="absolute -right-3 -top-3 flex h-14 w-14 animate-float items-center justify-center rounded-full bg-accent shadow-glow sm:-right-5 sm:-top-5">
            <MoonStar className="h-7 w-7 text-accent-foreground" aria-hidden />
          </span>
        </div>

        <div className="text-center lg:text-left">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-accent">
            <MoonStar className="h-3.5 w-3.5" aria-hidden />
            Nuestra historia
          </span>

          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-night-foreground sm:text-4xl">
            Inspirado en Luna
          </h2>

          <div className="mt-6 space-y-4 text-base leading-relaxed text-night-foreground/75 sm:text-lg">
            <p>
              PetMoon no es solo un servicio de transporte; es un homenaje. Nació del amor
              incondicional por mi perrita,{" "}
              <strong className="font-extrabold text-accent">Luna</strong>. Ella me enseñó
              que nuestras mascotas no son solo animales, son familia.
            </p>
            <p>
              Cuando Luna nos dejó, decidí transformar ese amor en un propósito: asegurar
              que cada peludo viaje con el mismo nivel de cuidado, seguridad y cariño que
              yo le daría a ella. En PetMoon, tratamos a tu mejor amigo como a uno de los
              nuestros, porque sabemos lo importantes que son.
            </p>
          </div>

          <p className="mt-7 inline-flex items-center gap-2.5 font-display text-lg font-bold text-accent sm:text-xl">
            <Heart className="h-5 w-5" fill="currentColor" aria-hidden />
            Cada viaje es guiado por la luz de Luna.
          </p>
        </div>
      </div>
    </section>
  );
}
